import { imageVariantLink } from '@/common/media';

import type { Person } from '../persons/person.dto';

import {
  type CreateEducation,
  type CreateEducationDto,
  type CreateExperience,
  type CreateExperienceDto,
  type CrewFilters,
  type CrewFiltersDto,
  type SingleCrew,
  type SingleCrewDto,
  type CreateUpdateProfile,
  type UpdateProfileDto,
  type CrewFileDto,
  type CrewFile,
  type CrewRate,
  type CrewRateDto,
  type RateAmount,
  CrewVisibility,
  RateType,
} from './crew.types';

/** The order a profile's rate is read in when it still carries more than one. */
const RATE_PERIODS = [RateType.DAY, RateType.MONTH, RateType.HOUR] as const;

const mapDtoToRate = (rate: CrewRateDto | null): CrewRate => {
  const amount = (value: CrewRateDto[RateType] | undefined): RateAmount | null =>
    value ? { currency: value.currency, number: +value.number } : null;

  return {
    hour: amount(rate?.hour),
    day: amount(rate?.day),
    month: amount(rate?.month),
    negotiable: !!rate?.negotiable,
  };
};

/**
 * The period a profile is paid by and what it is: the first one set to more than nothing. Zero
 * counts as unset — profiles used to be created with all three at 0.
 */
export const primaryRate = (rate: CrewRate | null | undefined) => {
  const period = RATE_PERIODS.find((p) => (rate?.[p]?.number ?? 0) > 0);
  return period && rate?.[period] ? { period, ...rate[period] } : null;
};

const mapRateToForm = (rate: CrewRate | null | undefined): CreateUpdateProfile['rate'] => {
  const primary = primaryRate(rate);
  const negotiable = !!rate?.negotiable;
  return primary
    ? { number: primary.number, currency: primary.currency, period: primary.period, negotiable }
    : { number: null, currency: 'USD', period: RateType.DAY, negotiable };
};

/** Negotiable states no amount, so the API gets none even if the form still holds one. */
const mapRateToDto = ({
  number,
  currency,
  period,
  negotiable,
}: CreateUpdateProfile['rate']): CrewRateDto => {
  const amount = (p: RateType) =>
    !negotiable && p === period && number != null
      ? { currency: currency || 'USD', number: String(number) }
      : null;

  return {
    hour: amount(RateType.HOUR),
    day: amount(RateType.DAY),
    month: amount(RateType.MONTH),
    negotiable,
  };
};

const mapDtoToFile = (file: CrewFileDto): CrewFile => ({
  contentType: file.content_type,
  link: file.link,
  name: file.name,
  size: file.size,
});

export const mapDtoToCrew = ({
  location,
  created_at,
  cruising_experience,
  has_driver_licence,
  person_id,
  recommendation_files,
  phone_number,
  updated_at,
  educations,
  first_name,
  last_name,
  years_of_experience,
  years_of_experience_calculated,
  professional_profile,
  preferred_contact_method,
  experiences,
  cv,
  photo,
  liked,
  rate,
  email,
  work_types,
  available_from,
  open_to_temporary,
  ...crew
}: SingleCrewDto): SingleCrew => ({
  ...crew,
  email: email ?? '',
  workTypes: work_types ?? [],
  availableFrom: available_from ?? null,
  openToTemporary: open_to_temporary ?? false,
  createdAt: created_at,
  cruisingExperience: cruising_experience,
  hasDriverLicence: has_driver_licence,
  personId: person_id,
  phoneNumber: phone_number,
  recommendationFiles: recommendation_files,
  updatedAt: updated_at,
  location: {
    ...location,
    placeId: location.place_id,
    zipCode: location.zip_code,
  },
  educations: educations.map(({ profile_id, university_name, ...e }) => ({
    ...e,
    profileId: profile_id,
    universityName: university_name,
  })),
  firstName: first_name,
  lastName: last_name,
  yearsOfExperience: years_of_experience,
  professionalProfile: professional_profile,
  preferredContactMethod: preferred_contact_method ?? null,
  experiences: experiences.map(
    ({
      employment_type,
      profile_id,
      references_id,
      yacht_info,
      recommendation_file,
      title,
      ...experience
    }) => ({
      ...experience,
      employmentType: employment_type,
      profileId: profile_id,
      referencesId: references_id,
      yachtInfo: yacht_info,
      title: title.split(', '),
      recommendationFile: recommendation_file ? mapDtoToFile(recommendation_file) : null,
    }),
  ),
  cv: cv ? mapDtoToFile(cv) : null,
  // The largest it is drawn at is the CV page's 96 px portrait.
  photo: imageVariantLink(photo, 96),
  isFavorite: liked,
  rate: mapDtoToRate(rate),
  yearsOfExperienceCalculated: years_of_experience_calculated,
});

export const mapFiltersToDto = ({
  search,
  isFavorite,
  city,
  rateMax,
  rateMin,
  rateType,
  skills,
  experienceYears,
  positions,
  openToTemporary,
}: CrewFilters): CrewFiltersDto => ({
  search: search || undefined,
  starred: isFavorite ? true : undefined,
  city: city || undefined,
  rate_max: rateMax || undefined,
  rate_min: rateMin || undefined,
  rate_type: rateType || undefined,
  skills: skills && skills.length > 0 ? skills : undefined,
  experience_years: experienceYears || undefined,
  roles: positions && positions.length > 0 ? positions : undefined,
  open_to_temporary: openToTemporary ? true : undefined,
});

export const mapCreateEducationToDto = ({
  universityName,
  personId: _,
  ...c
}: CreateEducation): CreateEducationDto => ({
  ...c,
  university_name: universityName,
});

export const mapCreateUpdateProfileToDto = ({
  cruisingExperience,
  hasDriverLicence,
  phoneNumber,
  professionalProfile,
  preferredContactMethod,
  yearsOfExperience,
  location,
  rate,
  email,
  workTypes,
  availableFrom,
  // Not sent: the service works it out from the work types.
  openToTemporary: _openToTemporary,
  ...profile
}: CreateUpdateProfile): UpdateProfileDto => ({
  ...profile,
  email: email || null,
  cruising_experience: cruisingExperience,
  has_driver_licence: hasDriverLicence,
  phone_number: phoneNumber || null,
  professional_profile: professionalProfile,
  preferred_contact_method: preferredContactMethod ?? null,
  work_types: workTypes ?? [],
  available_from: availableFrom ?? null,
  years_of_experience: yearsOfExperience,
  location: {
    address: location.address,
    city: location.city,
    latitude: location.latitude,
    longitude: location.longitude,
    name: location.name,
    place_id: location.placeId,
    state: location.state,
    zip_code: location.zipCode,
  },
  rate: mapRateToDto(rate),
});

export const mapSingleCrewToUpdateProfile = ({
  cruisingExperience,
  email,
  hasDriverLicence,
  licence,
  location,
  phoneNumber,
  positions,
  professionalProfile,
  preferredContactMethod,
  rate,
  skills,
  smoker,
  tattoos,
  visibility,
  firstName,
  lastName,
  yearsOfExperience,
  gender,
  workTypes,
  availableFrom,
  openToTemporary,
}: SingleCrew): CreateUpdateProfile => ({
  cruisingExperience: cruisingExperience ?? [],
  email,
  hasDriverLicence,
  licence,
  location,
  phoneNumber: phoneNumber ?? '+1',
  positions,
  professionalProfile,
  preferredContactMethod,
  rate: mapRateToForm(rate),
  skills,
  smoker,
  tattoos,
  visibility,
  yearsOfExperience,
  gender,
  firstName,
  lastName,
  workTypes,
  availableFrom,
  openToTemporary,
});

export const generateCreateCrewProfile = (user: null | Person): CreateUpdateProfile => ({
  cruisingExperience: [],
  email: user?.contacts?.find(({ contact_type }) => contact_type === 'email')?.contact ?? '',
  positions: [],
  skills: [],
  visibility: CrewVisibility.PUBLIC,
  hasDriverLicence: false,
  licence: [],
  location: {
    name: 'Location name',
    address: '',
    city: '',
    latitude: 0,
    longitude: 0,
    placeId: null,
    state: '',
    zipCode: '',
  },
  phoneNumber:
    user?.contacts?.find(({ contact_type }) => contact_type === 'phone')?.contact ?? '+1',
  professionalProfile: '',
  preferredContactMethod: null,
  rate: mapRateToForm(null),
  smoker: false,
  tattoos: false,
  gender: user?.person_info.gender ?? '',
  firstName: user?.person_info.first_name ?? '',
  lastName: user?.person_info.last_name ?? '',
  yearsOfExperience: undefined,
  workTypes: [],
  availableFrom: null,
  openToTemporary: false,
});

export const mapCreateExperienceToDto = ({
  employmentType,
  yachtInfo,
  title,
  ...experience
}: CreateExperience): CreateExperienceDto => ({
  ...experience,
  employment_type: employmentType,
  yacht_info: yachtInfo,
  references_id: null,
  title: title.join(', '),
});
